import { DOC_TYPES, type KbAudience, type KbDocType, type KbScope, type KbStatus, type SearchResult } from "./types";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { KbLibrarySectionResultsList } from "./KbLibrarySectionResultsList";
import { SectionCard } from "./SectionCard";

type Props = {
  q: string;
  searchAudience: "" | KbAudience;
  searchScope: "" | KbScope;
  searchDocType: "" | KbDocType;
  searchStatus: "" | KbStatus;
  results: SearchResult[];
  selected: SearchResult | null;
  draftCount: number;
  draftsLoading: boolean;
  canPublishKb: boolean;
  publishWorking: boolean;
  deleteWorking: boolean;
  confirmPublishSlug: string | null;
  confirmDeleteSlug: string | null;
  onQueryChange: (value: string) => void;
  onSearchAudienceChange: (value: "" | KbAudience) => void;
  onSearchScopeChange: (value: "" | KbScope) => void;
  onSearchDocTypeChange: (value: "" | KbDocType) => void;
  onSearchStatusChange: (value: "" | KbStatus) => void;
  onSearch: () => void;
  onResetFilters: () => void;
  onLoadDrafts: () => void;
  onLoadArticle: (slug: string, title: string) => void;
  onConfirmPublish: (slug: string | null) => void;
  onConfirmDelete: (slug: string | null) => void;
  onPublishDraft: (slug: string) => void;
  onSoftDelete: (slug: string) => void;
  onOpenEditorMode: () => void;
  showQuickAuditControls: boolean;
  quickAuditEnabled: boolean;
  quickAuditRisk: "all" | "low" | "moderate" | "healthy";
  onQuickAuditEnabledChange: (value: boolean) => void;
  onQuickAuditRiskChange: (value: "all" | "low" | "moderate" | "healthy") => void;
  onRunQuickAudit: () => void;
  /** platform_dev — open Dev tab rescope tool pre-filled from this row */
  showDevRescopePill?: boolean;
  devRescopeBusy?: boolean;
  onDevRescopeArticle?: (result: SearchResult) => void | Promise<void>;
};

export function KbLibrarySection(props: Props) {
  // Layout: mobile (<lg) stacks controls above results in one column.
  // Desktop (lg:+) is desktop-layout-pass Slice 5: search controls in a
  // sticky left rail (col-span-4) and the results list in the right column
  // (col-span-8) with no inner `max-h` scroll so results can use the page's
  // natural scroll. Per docs/planning/desktop_layout_pass.md §4.1.1 row 5.
  return (
    <SectionCard
      title="Library"
      description="Find existing articles, jump into drafts, and load an item into the editor."
      rightSlot={<span className="text-xs text-gray-500 dark:text-zinc-400">{props.results.length ? `${props.results.length} result${props.results.length === 1 ? "" : "s"}` : `${props.draftCount} draft${props.draftCount === 1 ? "" : "s"}`}</span>}
    >
      <div className="lg:grid lg:grid-cols-12 lg:gap-4 lg:items-start">
        <div className="space-y-3 lg:col-span-4 lg:sticky lg:top-4">
          <input
            {...{ [FIREFLY_TARGET_ATTR]: "kb-search" }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
            value={props.q}
            onChange={(event) => props.onQueryChange(event.target.value)}
            placeholder="Search by title or slug"
          />

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
            <select className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100" value={props.searchAudience} onChange={(event) => props.onSearchAudienceChange(event.target.value as "" | KbAudience)}>
              <option value="">all audiences</option>
              <option value="internal">internal</option>
              <option value="customer">customer</option>
              <option value="both">both</option>
              <option value="any">any</option>
            </select>
            <select className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100" value={props.searchScope} onChange={(event) => props.onSearchScopeChange(event.target.value as "" | KbScope)}>
              <option value="">all scopes</option>
              <option value="manufacturer">manufacturer</option>
              <option value="app">app</option>
              <option value="org">org</option>
            </select>
            <select className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100" value={props.searchDocType} onChange={(event) => props.onSearchDocTypeChange(event.target.value as "" | KbDocType)}>
              <option value="">all document types</option>
              {DOC_TYPES.map((docType) => <option key={docType} value={docType}>{docType}</option>)}
            </select>
            <select className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100" value={props.searchStatus} onChange={(event) => props.onSearchStatusChange(event.target.value as "" | KbStatus)}>
              <option value="">all statuses</option>
              <option value="published">published</option>
              <option value="draft">draft</option>
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "kb-search-submit" }}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
              onClick={props.onSearch}
            >
              Search library
            </button>
            <button type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200" onClick={props.onResetFilters}>
              Clear filters
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
              onClick={props.onLoadDrafts}
              disabled={props.draftsLoading}
            >
              {props.draftsLoading ? "Loading drafts…" : "Show drafts"}
            </button>
            {props.showQuickAuditControls ? (
            <button
              type="button"
              className={
                "rounded-lg border px-3 py-2 text-sm " +
                (props.quickAuditEnabled
                  ? "border-indigo-500 bg-indigo-50 text-indigo-900 dark:border-indigo-500 dark:bg-indigo-950/40 dark:text-indigo-200"
                  : "border-gray-300 bg-white dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200")
              }
              onClick={() => props.onQuickAuditEnabledChange(!props.quickAuditEnabled)}
            >
              {props.quickAuditEnabled ? "Chunk audit on" : "Chunk audit"}
            </button>
            ) : null}
            {props.showQuickAuditControls && props.quickAuditEnabled ? (
              <>
                <select
                  className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                  value={props.quickAuditRisk}
                  onChange={(event) => props.onQuickAuditRiskChange(event.target.value as "all" | "low" | "moderate" | "healthy")}
                >
                  <option value="all">All chunks</option>
                  <option value="low">Low chunks</option>
                  <option value="moderate">Moderate chunks</option>
                  <option value="healthy">Healthy chunks</option>
                </select>
                <button
                  type="button"
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
                  onClick={props.onRunQuickAudit}
                >
                  Run chunk audit
                </button>
              </>
            ) : null}
          </div>
        </div>

        <div className="mt-3 lg:mt-0 lg:col-span-8">
          <KbLibrarySectionResultsList
            results={props.results}
            selected={props.selected}
            canPublishKb={props.canPublishKb}
            publishWorking={props.publishWorking}
            deleteWorking={props.deleteWorking}
            confirmPublishSlug={props.confirmPublishSlug}
            confirmDeleteSlug={props.confirmDeleteSlug}
            quickAuditEnabled={props.quickAuditEnabled}
            onLoadArticle={props.onLoadArticle}
            onOpenEditorMode={props.onOpenEditorMode}
            onConfirmPublish={props.onConfirmPublish}
            onConfirmDelete={props.onConfirmDelete}
            onPublishDraft={props.onPublishDraft}
            onSoftDelete={props.onSoftDelete}
            showDevRescopePill={props.showDevRescopePill}
            devRescopeBusy={props.devRescopeBusy}
            onDevRescopeArticle={props.onDevRescopeArticle}
            scrollContainerClassName="max-h-[32rem] overflow-y-auto pr-1 lg:max-h-none lg:overflow-visible lg:pr-0"
          />
        </div>
      </div>
    </SectionCard>
  );
}